import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { ArrowRight, ExternalLink, Building, Heart, Users, Activity, Briefcase, Compass, Leaf, ShieldCheck, Gamepad2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryInfo = {
  'Ministry': {
    icon: Heart,
    color: 'bg-purple-100 text-purple-700',
    description: 'Faith-based organizations serving communities'
  },
  'Nonprofit': {
    icon: Users,
    color: 'bg-green-100 text-green-700',
    description: 'Community impact and social good initiatives'
  },
  'Business': {
    icon: Building,
    color: 'bg-[#EFF6FF] text-[#1D4ED8]',
    description: 'Revenue-generating ventures across industries'
  },
  'Health & Wellness': {
    icon: Activity,
    color: 'bg-emerald-100 text-emerald-700',
    description: 'Fitness, nutrition, and whole-person wellness'
  },
  'Career Development': {
    icon: Briefcase,
    color: 'bg-sky-100 text-sky-700',
    description: 'Career advancement and workforce development'
  },
  'Life & Purpose Coaching': {
    icon: Compass,
    color: 'bg-amber-100 text-amber-700',
    description: 'Helping people discover and live their purpose'
  },
  'Fresh Local Produce': {
    icon: Leaf,
    color: 'bg-lime-100 text-lime-700',
    description: 'Family-grown, community-sourced food'
  },
  'Insurance Products': {
    icon: ShieldCheck,
    color: 'bg-rose-100 text-rose-700',
    description: 'Insurance products, education, and coverage'
  },
  'Digital Universe': {
    icon: Gamepad2,
    color: 'bg-indigo-100 text-indigo-700',
    description: 'Where kids learn, play, create, and explore'
  }
};

export default function BrandsPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/brands`);
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const categories = ['All', ...new Set(brands.map(b => b.category))];
  
  const filteredBrands = activeCategory === 'All' 
    ? brands 
    : brands.filter(b => b.category === activeCategory);

  const getCategoryInfo = (category) => categoryInfo[category] || categoryInfo['Business'];

  if (loading) {
    return (
      <Layout title="Brands" description="Explore the DCA Enterprises family of brands and businesses.">
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Brands" description="Explore the DCA Enterprises family of brands and businesses.">
      {/* Hero Section */}
      <section className="section-padding bg-white" data-testid="brands-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="overline mb-4 inline-block">Brand Ecosystem</span>
            <h1 className="heading-1 text-4xl sm:text-5xl lg:text-6xl text-[#0A0F1C] mb-6" data-testid="brands-title">
              A Portfolio of <span className="text-[#1D4ED8]">Purpose-Driven</span> Ventures
            </h1>
            <p className="body-text text-[#475569] text-lg mb-4">
              These ventures are not distractions. <strong>They are real-world proof</strong> of the systems, strategy,
              and structure David teaches entrepreneurs every day. From ministry to business, each one was built using the
              same 7-stage framework we use with clients.
            </p>
            <p className="body-text text-[#475569] text-base mb-8">
              From ministry to business, each venture represents a commitment to impact, service, and excellence.
            </p>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mt-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 font-bold text-sm transition-all ${
                  activeCategory === category
                    ? 'bg-[#1D4ED8] text-white'
                    : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
                data-testid={`category-filter-${category.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="brands-grid">
        <div className="container-custom">
          {/* Category Stats - only show categories that have at least one brand */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {Object.entries(categoryInfo)
              .map(([category, info]) => ({ category, info, count: brands.filter(b => b.category === category).length }))
              .filter(({ count }) => count > 0)
              .map(({ category, info, count }) => {
              const Icon = info.icon;
              return (
                <div 
                  key={category}
                  className="bg-white p-6 border border-[#E2E8F0] rounded-lg shadow-sm"
                  data-testid={`category-stat-${category.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${info.color.split(' ')[0]}`}>
                      <Icon size={24} className={info.color.split(' ')[1]} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#0A0F1C]">{count}</div>
                      <div className="text-sm text-[#475569]">{category}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Brands Bento Grid */}
          <div className="bento-grid">
            {filteredBrands.map((brand, index) => {
              const info = getCategoryInfo(brand.category);
              const Icon = info.icon;
              const isLarge = index === 0 || index === 3;
              
              return (
                <div 
                  key={brand.brand_id || index}
                  className={`bg-white p-8 border border-[#E2E8F0] hover:shadow-xl hover:-translate-y-1 transition-all ${
                    isLarge ? 'lg:col-span-2' : ''
                  }`}
                  data-testid={`brand-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full ${info.color}`}>
                      <Icon size={14} />
                      {brand.category}
                    </span>
                    {brand.website_url && (
                      <a 
                        href={brand.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#94A3B8] hover:text-[#1D4ED8] transition-colors"
                        data-testid={`brand-external-link-${index}`}
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                  
                  <h3 className={`heading-3 ${isLarge ? 'text-2xl' : 'text-xl'} text-[#0A0F1C] mb-3`}>
                    {brand.name}
                  </h3>
                  
                  <p className="text-[#475569] mb-6 leading-relaxed">{brand.description}</p>
                  
                  {brand.website_url ? (
                    <a 
                      href={brand.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#1D4ED8] font-bold text-sm hover:gap-3 transition-all"
                      data-testid={`brand-visit-link-${index}`}
                    >
                      Visit Website <ExternalLink size={14} />
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[#94A3B8] text-sm font-medium">
                      Website Coming Soon
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Message Section */}
      <section className="section-padding bg-white" data-testid="brands-message">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline mb-4 inline-block">Why This Matters</span>
            <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-6">
              Real Experience, Real Results
            </h2>
            <p className="text-[#475569] text-lg mb-8">
              When I share business strategies with you, they come from hands-on experience building 
              and operating these ventures. I don't just teach theory - I've lived it. This portfolio 
              demonstrates that the frameworks and systems I share actually work.
            </p>
            <Link 
              to="/about" 
              className="text-[#1D4ED8] font-bold hover:underline inline-flex items-center gap-2"
            >
              Learn More About My Journey <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1D4ED8]" data-testid="brands-cta">
        <div className="container-custom text-center">
          <h2 className="heading-2 text-3xl lg:text-4xl text-white mb-4">
            Ready to Build Your Own Portfolio?
          </h2>
          <p className="text-[#BFDBFE] text-lg mb-8 max-w-2xl mx-auto">
            Let me help you create and grow businesses that make an impact. 
            Start with a strategy session.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/schedule" 
              className="btn-primary-orange inline-flex items-center justify-center gap-2"
              data-testid="brands-cta-book"
            >
              FREE 20-Minute Business Jumpstart Session
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/services" 
              className="bg-white/10 border border-white/30 text-white px-8 py-4 font-bold text-sm hover:bg-white/20 transition-colors inline-flex items-center justify-center"
              data-testid="brands-cta-services"
            >
              View Services
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
